import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Box,
  Button,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { ArrowLeft } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import dayjs from 'dayjs';
import 'dayjs/locale/pt-br';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import Loading from '../components/loading';
import ErrorMessage from '../components/errorMessage';
import { useDashboardAppointmentList } from './hooks/useDashboardAppointmentList';
import DashboardPatientAppointmentGroup from './DashboardPatientAppointmentGroup';

dayjs.extend(customParseFormat);
dayjs.locale('pt-br');

export default function DashboardAppointmentList({
  title,
  endpoint,
  emptyMessage,
  showCancellationReason,
}) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const monthParam = searchParams.get('month');

  const selectedMonth = useMemo(() => {
    const parsed = dayjs(monthParam, 'YYYY-MM', true);
    return parsed.isValid() ? parsed.startOf('month') : dayjs().startOf('month');
  }, [monthParam]);
  const startDate = useMemo(() => selectedMonth.startOf('month'), [selectedMonth]);
  const endDate = useMemo(() => selectedMonth.endOf('month'), [selectedMonth]);

  const { appointments, isLoading, error } = useDashboardAppointmentList({
    endpoint,
    startDate,
    endDate,
  });
  const [showErrorMessage, setShowErrorMessage] = useState(false);

  useEffect(() => {
    if (error) {
      setShowErrorMessage(true);
    }
  }, [error]);

  const periodLabel = selectedMonth.format('MMMM [de] YYYY');
  const columnCount = showCancellationReason ? 10 : 9;

  // Groups by patient keeping the API date order; recurrent patients come first
  const patientGroups = useMemo(() => {
    const groupsByPatient = new Map();
    appointments.forEach((appointment) => {
      const group = groupsByPatient.get(appointment.patientId) ?? [];
      group.push(appointment);
      groupsByPatient.set(appointment.patientId, group);
    });
    return [...groupsByPatient.values()].sort((a, b) => b.length - a.length);
  }, [appointments]);

  return (
    <Box p={3}>
      <Loading isToHidden={!isLoading} />
      {showErrorMessage && (
        <ErrorMessage
          message={String(error)}
          show={showErrorMessage}
          setShow={setShowErrorMessage}
        />
      )}

      <Button
        startIcon={<ArrowLeft size={18} />}
        onClick={() => navigate(`/dashboard?month=${selectedMonth.format('YYYY-MM')}`)}
        sx={{ mb: 2 }}
      >
        Voltar para o dashboard
      </Button>

      <Typography variant="h6" fontWeight={600}>
        {title}
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3} textTransform="capitalize">
        {periodLabel}
        {!isLoading &&
          ` · ${appointments.length} consulta(s) de ${patientGroups.length} paciente(s)`}
      </Typography>

      <Card variant="outlined">
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox" />
                <TableCell>Paciente</TableCell>
                <TableCell>Telefone</TableCell>
                <TableCell>Data</TableCell>
                <TableCell>Horário</TableCell>
                <TableCell>Profissional</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Sala</TableCell>
                <TableCell>Observação</TableCell>
                {showCancellationReason && <TableCell>Motivo do cancelamento</TableCell>}
              </TableRow>
            </TableHead>
            <TableBody>
              {patientGroups.map((group) => (
                <DashboardPatientAppointmentGroup
                  key={group[0].patientId}
                  appointments={group}
                  showCancellationReason={showCancellationReason}
                />
              ))}
              {!isLoading && appointments.length === 0 && (
                <TableRow>
                  <TableCell colSpan={columnCount} align="center" sx={{ py: 4 }}>
                    <Typography color="text.secondary">{emptyMessage}</Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Card>
    </Box>
  );
}

DashboardAppointmentList.propTypes = {
  title: PropTypes.string.isRequired,
  endpoint: PropTypes.string.isRequired,
  emptyMessage: PropTypes.string.isRequired,
  showCancellationReason: PropTypes.bool,
};

DashboardAppointmentList.defaultProps = {
  showCancellationReason: false,
};
